import React, { useState, useCallback, useEffect } from 'react';
import { useMap } from '../map/MapContext';

// Raster chart layer IDs that are already added to the map in MapView on load.
// BasemapPanel only toggles their visibility; it does not add/remove them.
const AERONAUTICAL_CHART_IDS = ['vfr-sectional', 'tac', 'ifr-low', 'ifr-high', 'ifr-area', 'heli-route'];

const STORAGE_KEY = 'efb_basemap_state';

interface BasemapOption {
  id: string;
  label: string;
  thumbnail: string;
  /** If set, toggles the pre-existing aeronautical chart layer of that id. */
  chartLayerId?: string;
  /** Otherwise, provides a traditional sources+layers injection. */
  style?: () => { sources: Record<string, unknown>; layers: unknown[] };
}

const BASEMAPS: BasemapOption[] = [
  {
    id: 'local',
    label: 'Local',
    thumbnail: '▦',
    style: () => ({
      sources: {},
      layers: [{ id: 'local-base', type: 'background', paint: { 'background-color': '#101827' } }],
    }),
  },
  {
    id: 'osm',
    label: 'OpenStreetMap',
    thumbnail: '🗺️',
    style: () => ({
      sources: {
        'osm-raster': {
          type: 'raster',
          tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution: '© OpenStreetMap contributors',
        },
      },
      layers: [{ id: 'osm-base', type: 'raster', source: 'osm-raster' }],
    }),
  },
  {
    id: 'satellite',
    label: 'Satellite (ESRI)',
    thumbnail: '🛰️',
    style: () => ({
      sources: {
        'esri-sat': {
          type: 'raster',
          tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
          tileSize: 256,
          attribution: '© Esri, Maxar, Earthstar Geographics',
        },
      },
      layers: [{ id: 'esri-sat-base', type: 'raster', source: 'esri-sat' }],
    }),
  },
  {
    id: 'topo',
    label: 'Topo (ESRI)',
    thumbnail: '⛰️',
    style: () => ({
      sources: {
        'esri-topo': {
          type: 'raster',
          tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}'],
          tileSize: 256,
          attribution: '© Esri, HERE, Garmin',
        },
      },
      layers: [{ id: 'esri-topo-base', type: 'raster', source: 'esri-topo' }],
    }),
  },
  {
    id: 'dark',
    label: 'Dark (CartoDB)',
    thumbnail: '🌑',
    style: () => ({
      sources: {
        'carto-dark': { type: 'raster', tiles: ['https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png'], tileSize: 256 },
      },
      layers: [{ id: 'carto-dark-base', type: 'raster', source: 'carto-dark' }],
    }),
  },
  {
    id: 'light',
    label: 'Light (CartoDB)',
    thumbnail: '☀️',
    style: () => ({
      sources: {
        'carto-light': { type: 'raster', tiles: ['https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png'], tileSize: 256 },
      },
      layers: [{ id: 'carto-light-base', type: 'raster', source: 'carto-light' }],
    }),
  },
  // Aeronautical chart presets — these flip visibility on pre-added layers
  { id: 'vfr-sectional', label: 'VFR Sectional',   thumbnail: '🛰️', chartLayerId: 'vfr-sectional' },
  { id: 'tac',           label: 'Terminal Area',   thumbnail: '🏙️', chartLayerId: 'tac' },
  { id: 'ifr-low',       label: 'IFR Low Enroute', thumbnail: '📡', chartLayerId: 'ifr-low' },
  { id: 'ifr-high',      label: 'IFR High Enroute', thumbnail: '✈️', chartLayerId: 'ifr-high' },
  { id: 'ifr-area',      label: 'IFR Area',         thumbnail: '🗺️', chartLayerId: 'ifr-area' },
  { id: 'heli-route',    label: 'Heli Route',       thumbnail: '🚁', chartLayerId: 'heli-route' },
];

export const BasemapPanel: React.FC = () => {
  const { map } = useMap();
  const [active, setActive] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return BASEMAPS.some((basemap) => basemap.id === stored) ? stored! : 'osm';
    } catch { return 'osm'; }
  });
  const [opacity, setOpacityState] = useState<number>(0.85);

  const hideAllChartLayers = useCallback(() => {
    if (!map) return;
    for (const id of AERONAUTICAL_CHART_IDS) {
      try { map.setLayoutProperty(id, 'visibility', 'none'); } catch { /* ok */ }
    }
  }, [map]);

  const selectBasemap = useCallback((basemap: BasemapOption) => {
    if (!map) return;

    // Case 1: toggle a pre-added aeronautical chart layer
    if (basemap.chartLayerId) {
      hideAllChartLayers();
      try {
        map.setLayoutProperty(basemap.chartLayerId, 'visibility', 'visible');
        map.setPaintProperty(basemap.chartLayerId, 'raster-opacity', opacity);
      } catch (e) { console.warn('[efb] chart basemap toggle failed', e); }
      setActive(basemap.id);
      try { localStorage.setItem(STORAGE_KEY, basemap.id); } catch { /* ok */ }
      return;
    }

    // Case 2: legacy dynamic basemap injection
    if (!basemap.style) return;
    hideAllChartLayers();
    const { sources, layers } = basemap.style();
    const style = map.getStyle();
    if (style?.layers) {
      for (const l of style.layers) {
        if (l.id.endsWith('-base')) { try { map.removeLayer(l.id); } catch { /* ok */ } }
      }
    }
    for (const [key, spec] of Object.entries(sources)) {
      if (!map.getSource(key)) map.addSource(key, spec as any);
    }
    for (const layer of layers) {
      if (!map.getLayer((layer as any).id)) {
        const firstNonBase = map.getStyle().layers?.find((l) => !l.id.endsWith('-base'));
        map.addLayer(layer as any, firstNonBase?.id);
      }
    }
    setActive(basemap.id);
    try { localStorage.setItem(STORAGE_KEY, basemap.id); } catch { /* ok */ }
  }, [map, hideAllChartLayers, opacity]);

  // Apply opacity changes live for aeronautical charts
  useEffect(() => {
    if (!map) return;
    const b = BASEMAPS.find((x) => x.id === active);
    if (b?.chartLayerId) {
      try { map.setPaintProperty(b.chartLayerId, 'raster-opacity', opacity); } catch { /* ok */ }
    }
  }, [map, active, opacity]);

  return (
    <div>
      <div className="panel-section">
        <div className="panel-section-title">Select Basemap</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {BASEMAPS.map((b) => (
            <button
              key={b.id}
              className={`btn-secondary ${active === b.id ? 'active' : ''}`}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 8px',
                background: active === b.id ? '#1E3A5F' : undefined,
                borderColor: active === b.id ? 'var(--accent)' : undefined,
              }}
              onClick={() => selectBasemap(b)}
            >
              <span style={{ fontSize: 28 }}>{b.thumbnail}</span>
              <span style={{ fontSize: 11, marginTop: 4 }}>{b.label}</span>
            </button>
          ))}
        </div>
        {BASEMAPS.find((b) => b.id === active)?.chartLayerId && (
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 12 }}>Chart opacity: {Math.round(opacity * 100)}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round(opacity * 100)}
              onChange={(e) => setOpacityState(parseInt(e.target.value, 10) / 100)}
              style={{ width: '100%' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
